import { useEffect, useState } from 'preact/hooks';
import type { ActivityEntry, DaySchedule, PublicScheduleResponse } from '@crm-dance/public-api';
import { ApiError, getSchedule } from '../api';
import {
	buildBookingUrl,
	durationMinutes,
	formatDateSpan,
	formatDayHeading,
	formatTime12,
	visibleDays,
} from '../dates';

/**
 * Top-level list component for the schedule embed.
 *
 * <p>Owns the fetch lifecycle: an {@link AbortController} created in
 * {@code useEffect} aborts on unmount or when the slug changes. The
 * abort function is also surfaced to the host element as
 * {@code __crmTeardown} so the {@code MutationObserver} in {@code index.tsx}
 * can cancel in-flight fetches when the host SPA removes the container.
 *
 * <p>Co-locates {@code DayGroup} and {@code ActivityRow} below — they form
 * one cohesive render tree that's edited together. If filters land in v2
 * and the file grows past ~250 lines, that is the moment to fragment it,
 * not before. (See code-quality review F1 in the collegium synthesis.)
 *
 * @see docs/specs/schedule-widget-embed/technical.md § Fetch lifecycle
 */
export function ScheduleList({
	slug,
	hostRef,
	locationId,
}: {
	slug: string;
	hostRef: HTMLElement;
	locationId?: string;
}) {
	const [data, setData] = useState<PublicScheduleResponse | null>(null);
	const [error, setError] = useState<ApiError | null>(null);
	const [loading, setLoading] = useState(true);
	const [retryTick, setRetryTick] = useState(0);

	useEffect(() => {
		const ac = new AbortController();
		setLoading(true);
		setError(null);

		getSchedule(slug, ac.signal, locationId)
			.then((result) => {
				if (!ac.signal.aborted) setData(result);
			})
			.catch((e: Error) => {
				if (e.name === 'AbortError') return;
				setError(e instanceof ApiError ? e : new ApiError('network'));
			})
			.finally(() => {
				if (!ac.signal.aborted) setLoading(false);
			});

		// Surface teardown to the MutationObserver in index.tsx — when the host
		// SPA removes the container DOM node, we want the in-flight fetch to
		// abort rather than resolve into a detached shadow root.
		(hostRef as HTMLElement & { __crmTeardown?: () => void }).__crmTeardown = () => ac.abort();

		return () => ac.abort();
	}, [slug, hostRef, locationId, retryTick]);

	const days = visibleDays(data?.days);
	const events = data?.multiDayEvents ?? [];
	const total = days.reduce((sum, d) => sum + d.entries.length, 0) + events.length;

	return (
		<section class="section" aria-label="Class schedule" aria-busy={loading ? 'true' : 'false'}>
			{loading && <Skeleton />}

			{!loading && error?.kind === 'not-found' && <NotFoundState />}

			{!loading && error && error.kind !== 'not-found' && (
				<ErrorState onRetry={() => setRetryTick((t) => t + 1)} />
			)}

			{!loading && !error && total === 0 && <EmptyState />}

			{!loading && !error && total > 0 && (
				<>
					<span class="section-meta">all times shown in studio time</span>
					{events.map((event) => (
						<MultiDayBanner key={event.id} entry={event} slug={slug} />
					))}
					{days.map((day) => (
						<DayGroup key={day.date} day={day} slug={slug} />
					))}
				</>
			)}
		</section>
	);
}

// ── MultiDayBanner ─────────────────────────────────────────────

/**
 * One pinned banner per multi-day event, above the day groups. The server
 * de-dupes the span into a single {@code multiDayEvents[]} item — no per-day
 * collapse here. Shows the true inclusive span from {@code startDate}/{@code
 * endDate} (a today-forward window still reads "Jul 24 – 27"), and downgrades
 * the CTA to "Registration closed" once the event has ended.
 */
function MultiDayBanner({ entry, slug }: { entry: ActivityEntry; slug: string }) {
	const span = formatDateSpan(entry.startDate, entry.endDate);
	const isFull = entry.isFull === true;
	const isClosed = entry.registrationOpen === false;

	const href = buildBookingUrl(slug, entry.id ?? '');
	const ariaLabel = `Book ${entry.title ?? 'event'} — opens in new tab`;
	const rowStyle = entry.customTypeColor
		? ({ '--crm-row-color': entry.customTypeColor } as Record<string, string>)
		: undefined;

	const renderCta = () => {
		if (isClosed) {
			return (
				<span class="cta-disabled" aria-disabled="true">
					Registration closed
				</span>
			);
		}
		if (isFull) {
			return (
				<span class="cta-disabled" aria-disabled="true">
					Full
				</span>
			);
		}
		return <span class="cta-button">Book</span>;
	};

	return (
		<a
			class="row banner"
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			aria-label={ariaLabel}
			style={rowStyle}
		>
			<div class="row-time">
				<div class="row-time-start">{span}</div>
				<div class="row-time-duration">All day</div>
			</div>
			<div class="row-body">
				<div class="row-title">{entry.title}</div>
				{entry.instructors && entry.instructors.length > 0 && (
					<div class="row-instructor">with {entry.instructors.join(', ')}</div>
				)}
			</div>
			<div class="row-cta">{renderCta()}</div>
		</a>
	);
}

// ── DayGroup ───────────────────────────────────────────────────

function DayGroup({ day, slug }: { day: DaySchedule & { entries: ActivityEntry[] }; slug: string }) {
	return (
		<div class="day">
			<div class="day-heading">{formatDayHeading(day.date ?? '')}</div>
			{day.entries.map((entry) => (
				<ActivityRow key={entry.id} entry={entry} slug={slug} />
			))}
		</div>
	);
}

// ── ActivityRow ────────────────────────────────────────────────

/**
 * One class row in the list. Renders as an {@code <a target="_blank">} so
 * keyboard and screen-reader users get native link semantics.
 *
 * <p>Nullable-field handling per design.md § Nullable response fields:
 * empty {@code instructors} hides the instructor row, missing
 * {@code endTime} hides the duration.
 *
 * <p>The {@code customTypeColor} from the response is plumbed through
 * to CSS via the {@code --crm-row-color} custom property on the row
 * element. The narrow-container card layout in {@code styles.css}
 * paints it as a 4px stripe before the time text, giving each row a
 * per-type visual anchor on mobile.
 */
function ActivityRow({ entry, slug }: { entry: ActivityEntry; slug: string }) {
	const start = formatTime12(entry.startTime ?? '');
	const dur = entry.endTime ? durationMinutes(entry.startTime ?? '', entry.endTime) : 0;
	const instructorText =
		entry.instructors && entry.instructors.length > 0 ? entry.instructors.join(', ') : null;
	const isFull = entry.isFull === true;

	const href = buildBookingUrl(slug, entry.id ?? '');
	const ariaLabel = `Book ${entry.title ?? 'class'} — opens in new tab`;

	// CSS custom-property carrier for the per-class-type color stripe.
	// `Record<string, string>` cast lets us set a non-standard property
	// name through React/Preact's style prop without fighting the type.
	const rowStyle = entry.customTypeColor
		? ({ '--crm-row-color': entry.customTypeColor } as Record<string, string>)
		: undefined;

	const renderCta = () => {
		if (isFull) {
			return (
				<span class="cta-disabled" aria-disabled="true">
					Full
				</span>
			);
		}
		return <span class="cta-button">Book</span>;
	};

	return (
		<a
			class="row"
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			aria-label={ariaLabel}
			style={rowStyle}
		>
			<div class="row-time">
				<div class="row-time-start">{start}</div>
				{dur > 0 && <div class="row-time-duration">{dur} min</div>}
			</div>
			<div class="row-body">
				<div class="row-title">{entry.title}</div>
				{instructorText && <div class="row-instructor">with {instructorText}</div>}
			</div>
			<div class="row-cta">{renderCta()}</div>
		</a>
	);
}

// ── States ─────────────────────────────────────────────────────

function Skeleton() {
	return (
		<div aria-label="Loading class schedule">
			<div class="skeleton" />
			<div class="skeleton" />
			<div class="skeleton" />
		</div>
	);
}

function EmptyState() {
	return (
		<div class="message">
			<div class="message-strong">No upcoming classes</div>
			<div>Check back soon!</div>
		</div>
	);
}

function NotFoundState() {
	return (
		<div class="message">
			<div class="message-strong">Studio not found</div>
		</div>
	);
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
	return (
		<div class="error" role="alert" aria-live="polite">
			<div class="error-text">Schedule is currently unavailable.</div>
			<button type="button" class="retry-button" onClick={onRetry}>
				Retry
			</button>
		</div>
	);
}
